/** @jsx jsx */
import React from "react";
import { createRoot } from "react-dom/client";
import { jsx, Box, Input, Button, Flex, ThemeUIProvider } from "theme-ui";
import { Themed } from "@theme-ui/mdx";

import theme from "../gatsby-plugin-theme-ui";

const formId = "faf91300-30b2-11f0-b4e4-65e573b4df5f";

/** 
 * The EmailOctopus signup form injected using the horrible, official method,
 * just so that we can get the Captcha and prevent more bot signups.
 **/
const Newsletter = ({orientation}) => {
  const emailOctopusFormRef = React.useRef(null);

  React.useEffect(() => {
    // The following is the officially sanctioned method of inserting the signup form. 
    // It inserts a script which downloads some JS which then inserts a bunch of HTML
    // and styles into the DOM.
    // Script must be injected in useEffect and not just rendered by the component due
    // to how React works.
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://eocampaign1.com/form/" + formId + ".js";
    script.setAttribute("data-form", formId);
    emailOctopusFormRef.current.appendChild(script);

    let reactRoot;
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      const mainFormEl = document.querySelector(".emailoctopus-form .main-form");
      if (mainFormEl) {
        console.debug("Found injected EO form after " + attempts + " attempts");
        clearInterval(poll);
        reactRoot = customizeForm(mainFormEl, orientation);
      } else if (attempts >= 1000) {
        console.warn("Giving up finding EO form after " + attempts + " attempts");
        clearInterval(poll);
      }
    }, 10);
    
    return () => {
      reactRoot?.unmount();
    };
  // Orientation change on resize works despite the missing dependency warning...
  }, []);

  return (
    <Box>
      <Themed.p sx={{color: "textLight.3", fontSize: "body", mt: 0}}>
        Sign up for the mailing list to receive important Fileside news and updates.
      </Themed.p>
      <Box sx={{position: "relative"}}>
        <div ref={emailOctopusFormRef}>
          {/* Script will be injected here by useEffect */}
        </div>
      </Box>
    </Box>
  );
};

function customizeForm(mainFormEl, orientation) {
  // Prevent centering of the inputs
  mainFormEl.style.display = "block";
  const formContainerEl = document.querySelector(".form-container");
  if (formContainerEl) {
    formContainerEl.style.margin = "0";
  }

  // Prevent centering of success/error messages
  const successMessageEl = document.querySelector(".emailoctopus-success-message");
  if (successMessageEl) {
    successMessageEl.style.textAlign = "left";
  }
  const errorMessageEl = document.querySelector(".emailoctopus-error-message");
  if (errorMessageEl) {
    errorMessageEl.style.textAlign = "left";
  }

  // Take over the inner form area and render our own components instead of the
  // provided ones for correct styling. Unfortunately, EO also applies a bunch
  // of highly specific styles to just `input`, which we can't override in any other
  // way than the hacky style overrides here.
  const mainFormRoot = createRoot(mainFormEl);
  mainFormRoot.render(              
    <ThemeUIProvider theme={theme}>
      <Flex sx={{flexDirection: orientation, gap: 3, alignItems: "flex-start"}}>
        <Input
          type="email"
          placeholder="Email"
          defaultValue=""
          name="field_0"
          id="field_0"
          autoComplete="email"
          required
          sx={{
            alignSelf: "stretch",
            flexGrow: 1,
            "&&&&:focus": {
              borderColor: "var(--theme-ui-colors-primary)",
            }
          }}
          style={{
            backgroundColor: "var(--theme-ui-colors-backgroundLight-1)",
            fontSize: "14px",
            lineHeight: "inherit",
            fontFamily: "inherit",
            margin: 0
          }}
        />
        <Button
          variant="primary"
          type="submit"
          sx={{
            width: "190px", // to prevent width changing when we set the text programmatically
            flexGrow: 0,
          }}
        >
          Keep me up to date
        </Button>
      </Flex>
    </ThemeUIProvider>
  );

  return mainFormRoot;
}

export default Newsletter;
